import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { dayCity } from "../../lib/trip-route";
import type { Trip } from "../../types";
import { useDiscoverCities, useDiscoverItems } from "../../state/discover-context";
import { useTripActions } from "../../state/trip-context";
import { CityFilter } from "../city-filter";
import { useFocusTrap } from "../../hooks/use-focus-trap";
import { rankForVibes, stopChipOf, type StopChip } from "../../lib/vibe-match";
import { StopPrice } from "./stop-price";

type Chip = "you" | StopChip;
const CHIPS: readonly Chip[] = ["you", "eat", "do", "relax"];

/** "Foodie and Culture & history", in the guest's language. */
function joinList(parts: readonly string[], lang: string): string {
  try {
    return new Intl.ListFormat(lang, { style: "long", type: "conjunction" }).format(parts);
  } catch {
    return parts.join(", ");
  }
}

import { Icon } from "@majourneys/icons";
interface AddStopDialogProps {
  readonly trip: Trip;
  readonly dayId: string | null;
  readonly onClose: () => void;
}

export function AddStopDialog({ trip, dayId, onClose }: AddStopDialogProps) {
  const { t, i18n } = useTranslation();
  const { addStop, removeStop } = useTripActions();
  // "For you" first: the trip's vibes rank the list. Eat / Do / Relax are
  // plain filters for someone who already knows what the gap in the day is.
  const [chip, setChip] = useState<Chip>("you");
  const vibes = useMemo(() => trip.vibes ?? [], [trip.vibes]);
  const items = useDiscoverItems();
  const cities = useDiscoverCities();
  const [query, setQuery] = useState("");
  // Default the city filter to the day's city (its own, else the trip's) so
  // suggestions are scoped to where that day is, but only if it's a known
  // city in the list.
  const [citySlug, setCitySlug] = useState<string | null>(() => {
    const day = trip.days.find((d) => d.id === dayId);
    const slug = day ? dayCity(trip, day) : trip.city;
    return cities.some((c) => c.slug === slug) ? slug : null;
  });
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const activeDay = useMemo(
    () => trip.days.find((d) => d.id === dayId) ?? null,
    [trip.days, dayId],
  );

  // Stops already on the day we're adding to, keyed itemId → stopId. Scoped
  // to the active day so toggling a row never touches another day's stops
  // (the same place can legitimately live on more than one day).
  const activeDayStops = useMemo(() => {
    const map = new Map<string, string>();
    const day = trip.days.find((d) => d.id === dayId);
    if (day) {
      for (const stop of day.stops) {
        map.set(stop.itemId, stop.id);
      }
    }
    return map;
  }, [trip.days, dayId]);

  // Unique places already saved anywhere in the trip — drives the meta count.
  const tripItemCount = useMemo(() => {
    const ids = new Set<string>();
    for (const day of trip.days) {
      for (const stop of day.stops) {
        ids.add(stop.itemId);
      }
    }
    return ids.size;
  }, [trip.days]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matching = items.filter((item) => {
      if (citySlug && item.citySlug !== citySlug) return false;
      if (chip !== "you" && stopChipOf(item) !== chip) return false;
      if (q.length === 0) return true;
      const haystack =
        `${item.title} ${item.subtitle} ${item.location}`.toLowerCase();
      return haystack.includes(q);
    });
    return chip === "you" ? rankForVibes(matching, vibes) : matching;
  }, [items, query, citySlug, chip, vibes]);

  useFocusTrap(dialogRef, activeDay !== null);

  if (!activeDay) return null;
  if (typeof document === "undefined") return null;

  const dayNumber = trip.days.findIndex((d) => d.id === activeDay.id) + 1;
  const dayLabel = t("trip.day_label", { number: dayNumber });

  const handleToggle = (itemId: string) => {
    const stopId = activeDayStops.get(itemId);
    if (stopId) {
      removeStop(trip.id, activeDay.id, stopId);
    } else {
      addStop(trip.id, activeDay.id, itemId);
    }
  };

  const clearSearch = () => setQuery("");
  const resetFilters = () => {
    setQuery("");
    setCitySlug(null);
    setChip("you");
  };

  const emptyReason: "no-items" | "no-matches" =
    items.length === 0 ? "no-items" : "no-matches";

  return createPortal(
    <div
      className="mj-trip-dialog-backdrop mj-trip-dialog-backdrop--full"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="mj-trip-dialog mj-trip-dialog--wide mj-add-stop"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mj-add-stop-title"
      >
        <header className="mj-trip-dialog__header">
          <div>
            <span>{t("trip.add_stop")}</span>
            <strong id="mj-add-stop-title">{t("trip.to_day", { day: dayLabel })}</strong>
          </div>
          <button
            type="button"
            className="mj-trip-dialog__close"
            onClick={onClose}
            aria-label={t("common.close")}
          >
            <Icon name="ri-close-line" aria-hidden="true" />
          </button>
        </header>

        <div className="mj-add-stop__filters">
          <CityFilter
            cities={cities}
            selectedSlug={citySlug}
            onSelect={setCitySlug}
          />
          <label className="mj-add-stop__search">
            <Icon name="ri-search-line" aria-hidden="true" />
            <input
              type="search"
              placeholder={t("trip.search_places")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button
                type="button"
                className="mj-add-stop__search-clear"
                onClick={clearSearch}
                aria-label={t("trip.clear_search")}
              >
                <Icon name="ri-close-line" aria-hidden="true" />
              </button>
            )}
          </label>
        </div>

        <div
          className="mj-add-stop__chips"
          role="group"
          aria-label={t("trip.chips.label")}
        >
          {CHIPS.map((c) => (
            <button
              key={c}
              type="button"
              aria-pressed={chip === c}
              className={`mj-add-stop__chip${chip === c ? " is-active" : ""}`}
              onClick={() => setChip(c)}
            >
              {t(`trip.chips.${c}`)}
            </button>
          ))}
        </div>
        {chip === "you" && vibes.length > 0 && (
          <p className="mj-add-stop__hint">
            {t("trip.chips.you_hint", {
              vibes: joinList(
                vibes.map((v) => t(`trip.vibe.${v}`)),
                i18n.language?.slice(0, 2) || "en",
              ),
            })}
          </p>
        )}

        <div className="mj-add-stop__meta">
          <span>
            {t("trip.results_count", { count: filtered.length })}
          </span>
          {tripItemCount > 0 && (
            <span className="mj-add-stop__meta-added">
              {t("trip.in_trip_count", { count: tripItemCount })}
            </span>
          )}
        </div>

        <div className="mj-add-stop__list">
          {filtered.length === 0 ? (
            <div className="mj-add-stop__empty">
              <Icon name={emptyReason === "no-items"
                    ? "ri-compass-3-line"
                    : "ri-search-eye-line"} aria-hidden="true" />
              {emptyReason === "no-items" ? (
                <>
                  <p className="mj-add-stop__empty-title">{t("trip.empty_no_items_title")}</p>
                  <p className="mj-add-stop__empty-hint">
                    {t("trip.empty_no_items_hint")}
                  </p>
                </>
              ) : (
                <>
                  <p className="mj-add-stop__empty-title">
                    {query.trim()
                      ? t("trip.no_matches_query", { query: query.trim() })
                      : citySlug
                        ? t("trip.no_picks_in_city", { city: cities.find((c) => c.slug === citySlug)?.name ?? t("activity.this_city") })
                        : t("trip.no_picks_match")}
                  </p>
                  <p className="mj-add-stop__empty-hint">
                    {t("trip.try_different_filters")}
                  </p>
                  <button
                    type="button"
                    className="mj-add-stop__empty-reset"
                    onClick={resetFilters}
                  >
                    <Icon name="ri-refresh-line" aria-hidden="true" />
                    {t("trip.reset_filters")}
                  </button>
                </>
              )}
            </div>
          ) : (
            filtered.map((item) => {
              const isAdded = activeDayStops.has(item.id);
              return (
                <button
                  key={item.id}
                  type="button"
                  className={`mj-add-stop__item${isAdded ? " is-added" : ""}`}
                  onClick={() => handleToggle(item.id)}
                  aria-pressed={isAdded}
                  aria-label={isAdded ? t("trip.remove_item", { title: item.title }) : t("trip.add_item", { title: item.title })}
                >
                  <span
                    className="mj-add-stop__thumb"
                    style={{ backgroundImage: `url(${item.image})` }}
                    aria-hidden="true"
                  />
                  <span className="mj-add-stop__body">
                    <strong>{item.title}</strong>
                    <span>
                      <em>{item.subtitle}</em>
                      <Icon name="ri-map-pin-line" aria-hidden="true" />
                      {item.location}
                    </span>
                    <StopPrice item={item} />
                  </span>
                  <span className="mj-add-stop__add" aria-hidden="true">
                    <Icon name={isAdded ? "ri-check-line" : "ri-add-line"} />
                  </span>
                </button>
              );
            })
          )}
        </div>

        <footer className="mj-trip-dialog__footer mj-add-stop__footer">
          <button
            type="button"
            className="mj-trip-dialog__submit"
            onClick={onClose}
          >
            <Icon name="ri-check-line" aria-hidden="true" />
            {t("trip.done")}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
